import React from 'react'
import {
  List,
  ListItem,
  Button,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'
import Alert from '@mui/material/Alert'
import StockCardIngredientItem from 'components/stockCardItems/StockCardIngredientItem'
import { IStockCard } from 'interfaces/IStockCard'
import { IIngredient } from 'interfaces/IIngredient'
import LoadIngredientAutocomplete from 'shared/autocomplete/loadIngredientAutocomplete'
import { VALUE_OUT } from 'enums/stockCard'

interface Props {
  stockCardData: IStockCard
  inOutDirection: string
  ingredients: IIngredient[]
  onChange: (ingredientId: string, value: any, key: string) => void
  onPrefill: () => void
  onConfirm: () => void
  isLoading: boolean
  getValidationMessage: (stockCard: IStockCard) => string | null
  qtyLabelIn?: string
  qtyLabelOut?: string
  onAddIngredient: () => void
  onRemoveIngredient: (index: number) => void
  isSuperuser?: boolean
  onUpdate?: () => void
  isUpdating?: boolean
  onDelete?: () => void
  isEditing: boolean
  setIsEditing: (isEditing: boolean) => void
}

const getStockCardIngredientRowIdentifier = (
  stockCardIngredient: IStockCard['stockCardIngredients'][number],
  index: number
) =>
  stockCardIngredient.stockCardIngredientId
    ? `stockCardIngredientId:${stockCardIngredient.stockCardIngredientId}`
    : `stockCardIngredientIndex:${index}`

export default function IngredientsDialog(props: Props) {
  const {
    stockCardData,
    inOutDirection,
    ingredients,
    onChange,
    onPrefill,
    onConfirm,
    isLoading,
    getValidationMessage,
    onAddIngredient,
    onRemoveIngredient,
    isSuperuser,
    onUpdate,
    isUpdating,
    onDelete,
    isEditing,
    setIsEditing,
  } = props

  const canEdit = isSuperuser
  const validationMessage = getValidationMessage(stockCardData)

  return (
    <List>
      {canEdit && (
        <ListItem>
          <Box width="100%" display="flex" justifyContent="flex-end" gap={8}>
            {isEditing && (
              <Button
                variant="contained"
                color="secondary"
                onClick={onPrefill}
                disabled={isLoading}
              >
                Isi Semua Sesuai Jumlah Diminta
              </Button>
            )}
            <Button
              variant="outlined"
              color={isEditing ? 'secondary' : 'primary'}
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Cancel Edit' : 'Edit'}
            </Button>
          </Box>
        </ListItem>
      )}
      {!stockCardData.isConfirmed && !isEditing && (
        <ListItem>
          <Box width="100%" display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="secondary"
              onClick={onPrefill}
              disabled={isLoading}
            >
              {inOutDirection === VALUE_OUT
                ? 'Keluarkan Sesuai Permintaan'
                : 'Terima Sesuai Permintaan'}
            </Button>
          </Box>
        </ListItem>
      )}
      {stockCardData.stockCardIngredients.map((stockCardIngredient, idx) => (
        <ListItem key={idx} divider={idx !== stockCardData.stockCardIngredients.length - 1}>
          {!stockCardIngredient.ingredientId ? (
            <LoadIngredientAutocomplete
              onChange={(e: IIngredient) => {
                onChange(
                  getStockCardIngredientRowIdentifier(stockCardIngredient, idx),
                  e,
                  'ingredient'
                )
              }}
            />
          ) : (
            <StockCardIngredientItem
              key={
                stockCardIngredient.stockCardIngredientId ||
                idx
              }
              stockCardIngredient={stockCardIngredient}
              inOutDirection={inOutDirection}
              ingredients={ingredients}
              outgoingDestination={stockCardData.outgoingDestination}
              onChange={onChange}
              rowIdentifier={getStockCardIngredientRowIdentifier(
                stockCardIngredient,
                idx
              )}
              qtyLabelIn={props.qtyLabelIn}
              qtyLabelOut={props.qtyLabelOut}
              disabled={!!stockCardData.isConfirmed && !isEditing}
              isSuperuser={isSuperuser}
              isConfirmed={!!stockCardData.isConfirmed}
              isEditing={isEditing}
              onRemove={isEditing ? () => onRemoveIngredient(idx) : undefined}
            />
          )}
        </ListItem>
      ))}
      {isEditing && (
        <ListItem>
          <Button
            variant="outlined"
            color="primary"
            onClick={onAddIngredient}
          >
            Tambah Bahan Baku
          </Button>
        </ListItem>
      )}
      <ListItem>
        <Box display="flex" gap={8}>
          {isEditing ? (
            <>
              {onUpdate && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onUpdate}
                  disabled={isUpdating}
                  startIcon={
                    isUpdating ? <CircularProgress size={18} color="inherit" /> : null
                  }
                >
                  Update Stock Card
                </Button>
              )}
              {canEdit && onDelete && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onDelete}
                  disabled={isLoading || !!stockCardData.isConfirmed}
                >
                  Delete
                </Button>
              )}
            </>
          ) : (
            <>
              {!stockCardData.isConfirmed && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onConfirm}
                  disabled={
                    isLoading ||
                    (!!validationMessage && !stockCardData.isConfirmed)
                  }
                  startIcon={
                    isLoading ? <CircularProgress size={18} color="inherit" /> : null
                  }
                >
                  Save
                </Button>
              )}
            </>
          )}
        </Box>
      </ListItem>
      {isEditing && !!stockCardData.isConfirmed && (
        <ListItem>
          <Typography color="secondary" variant="body2">
            Kartu stok tidak dapat dihapus karena sudah dikonfirmasi.
          </Typography>
        </ListItem>
      )}
      {validationMessage &&
        !stockCardData.isConfirmed && (
          <Alert severity="warning">{validationMessage}</Alert>
        )}
    </List>
  );
}
